import type { UploadPreflightTarget } from '@/shared/apis/asset-upload/post-upload-preflight';

export const UPLOAD_PREFLIGHT_MIN_BYTES = 100 * 1024 * 1024;

export type UploadPreflightPolicyReason =
  | 'LARGE_BROWSER_DIRECT_UPLOAD'
  | 'FORCED'
  | 'BELOW_SIZE_THRESHOLD'
  | 'NOT_BROWSER_DIRECT'
  | 'DESKTOP_OR_RENDER_SERVICE'
  | 'URL_OR_EXISTING_CONTENT'
  | 'NO_FILE';

export interface UploadPreflightDecision {
  shouldRun: boolean;
  target: UploadPreflightTarget;
  uploadPath: string;
  policyReason: UploadPreflightPolicyReason;
}

export interface UploadPreflightPolicyInput {
  file?: Pick<File, 'size' | 'name' | 'type'> | null;
  target: UploadPreflightTarget;
  uploadPath: string;
  browserDirect: boolean;
  force?: boolean;
  exclusion?: Extract<
    UploadPreflightPolicyReason,
    'DESKTOP_OR_RENDER_SERVICE' | 'URL_OR_EXISTING_CONTENT'
  >;
}

const buildDecision = (
  input: Pick<UploadPreflightPolicyInput, 'target' | 'uploadPath'>,
  shouldRun: boolean,
  policyReason: UploadPreflightPolicyReason,
): UploadPreflightDecision => ({
  shouldRun,
  target: input.target,
  uploadPath: input.uploadPath,
  policyReason,
});

export const getUploadPreflightDecision = ({
  file,
  target,
  uploadPath,
  browserDirect,
  force = false,
  exclusion,
}: UploadPreflightPolicyInput): UploadPreflightDecision => {
  const decisionInput = { target, uploadPath };

  if (exclusion) {
    return buildDecision(decisionInput, false, exclusion);
  }
  if (!file) {
    return buildDecision(decisionInput, false, 'NO_FILE');
  }
  if (!browserDirect) {
    return buildDecision(decisionInput, false, 'NOT_BROWSER_DIRECT');
  }
  if (force) {
    return buildDecision(decisionInput, true, 'FORCED');
  }
  if (file.size < UPLOAD_PREFLIGHT_MIN_BYTES) {
    return buildDecision(decisionInput, false, 'BELOW_SIZE_THRESHOLD');
  }

  return buildDecision(decisionInput, true, 'LARGE_BROWSER_DIRECT_UPLOAD');
};
